import * as React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Typography,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";

import { ActiveRallyVideo } from "./ActiveRallyVideo";
import { AskAiPanel } from "./RallyColumnAskAIPanel";
import { KitchenArrivalRow, rallyKey, ReportResponse } from "./RallyBoardPopup";
import { CondensedRallyTimeline } from "./CondensedRallyTimeline";
import { SideoutGameInfluenceReport } from "./RallyPopup";

/* ----------------------------- helpers ----------------------------- */

export type Team01 = 0 | 1;
type TeamChoice = "both" | Team01;

type TeamCounts = { t0: number; t1: number; total?: number };

export const Team_0: Team01 = 0;
export const Team_1: Team01 = 1;

export const TEAM_RGB: Record<Team01, readonly [number, number, number]> = {
  0: [80, 160, 255], // ✅ matches your blue bars
  1: [120, 220, 140], // ✅ matches your green bars
} as const;

export function teamRgba(team: Team01, a = 1): string {
  const [r, g, b] = TEAM_RGB[team];
  return `rgba(${r},${g},${b},${a})`;
}

// Useful shared neutrals so you don't retype them everywhere
export const NEUTRAL_DIM = "rgba(255,255,255,0.12)";
export const NEUTRAL_TEXT = "rgba(0,0,0,0.70)";

type StakeKey = "opportunityLost" | "gamePointLost";

function toggleStakeFilter(active: StakeKey[], k: StakeKey): StakeKey[] {
  return active.includes(k) ? active.filter((x) => x !== k) : [...active, k];
}

type StakesFilter = "all" | "opportunityLost" | "gamePointLost";
type StakesKind = "opportunityLost" | "gamePointLost" | "other";

function stakesForErrTeam(row: any, errTeam: Team01): StakesKind {
  const servingTeam: Team01 | null =
    row?.servingTeam === 0 || row?.servingTeam === 1 ? row.servingTeam : null;

  const winnerTeam: Team01 | null =
    row?.winnerTeam === 0 || row?.winnerTeam === 1 ? row.winnerTeam : null;

  const scoredPoint: boolean | null =
    typeof row?.scoredPoint === "boolean" ? row.scoredPoint : null;

  if (servingTeam == null || winnerTeam == null || scoredPoint == null)
    return "other";

  // only meaningful if this errTeam actually LOST the rally
  if (winnerTeam === errTeam) return "other";

  const weWereServing = servingTeam === errTeam;

  // Sideout scoring semantics:
  // - If we were serving and lost, that’s an opportunity lost (no point scored).
  if (weWereServing && scoredPoint === false) return "opportunityLost";

  // - If opponent was serving and we lost, they scored a point (game point lost).
  if (!weWereServing && scoredPoint === true) return "gamePointLost";

  return "other";
}

const STORY_TO_RALLY_FILTER: Record<StoryFilterKey, RallyFilterKey> = {
  missedServe: "missedServe",
  missedReturn: "missedReturn",
  missed3rdDrop: "missedThirdDrop",
  missed3rdDrive: "missedThirdDrive",

  faultNet: "faultNet",
  faultOut: "faultOut",
  unforcedError: "unforcedError",
  forcedFault: "forcedFault",
  popupExploited: "popupExploited",
};

type StoryFilterKey =
  | "missedServe"
  | "missedReturn"
  | "missed3rdDrop"
  | "missed3rdDrive"
  | "faultNet"
  | "faultOut"
  | "unforcedError"
  | "forcedFault"
  | "popupExploited";

type RallyFilterKey =
  | "missedServe"
  | "missedReturn"
  | "missedThirdDrop"
  | "missedThirdDrive"
  | "faultNet"
  | "faultOut"
  | "unforcedError"
  | "forcedFault"
  | "popupExploited";

type StakeCounts = {
  rallyIndexes: Set<number>;
  t0: number;
  t1: number;
};

type FilterBucket = {
  key: StoryFilterKey;
  label: string;

  // all rallies matching this reason
  rallyIndexes: Set<number>;
  t0: number;
  t1: number;

  // NEW: reason × stake
  stakes: Record<StakeKey, StakeCounts>;
};

function otherTeam(t: Team01): Team01 {
  return t === 0 ? 1 : 0;
}

/**
 * Extract any “factor-ish” strings that might already be on KitchenArrivalRow.
 * (This is intentionally defensive so it works with whatever shape you currently have.)
 */
function getFactorStrings(row: any): string[] {
  const out: string[] = [];

  const push = (v: any) => {
    if (typeof v === "string") {
      const s = v.trim();
      if (s) out.push(s);
    }
  };

  // shallow common-ish
  const pushArr = (v: any) => {
    if (Array.isArray(v)) v.forEach(push);
  };

  // recursive: pull strings from nested objects/arrays (bounded depth)
  const walk = (v: any, depth: number) => {
    if (depth <= 0) return;
    if (v == null) return;

    if (typeof v === "string") {
      push(v);
      return;
    }
    if (Array.isArray(v)) {
      for (const it of v) walk(it, depth - 1);
      return;
    }
    if (typeof v === "object") {
      // If objects contain common label-ish fields, grab those
      push(v.label);
      push(v.name);
      push(v.title);
      push(v.text);
      push(v.reason);
      push(v.tag);

      // walk all values
      for (const key of Object.keys(v)) {
        walk((v as any)[key], depth - 1);
      }
    }
  };

  // old places you already tried
  pushArr(row?.factors);
  pushArr(row?.factorStrings);
  pushArr(row?.factorLabels);
  pushArr(row?.tags);
  pushArr(row?.reasons);
  pushArr(row?.triageTags);
  pushArr(row?.notes);

  // common nested
  pushArr(row?.debug?.factors);
  pushArr(row?.debug?.tags);

  // ✅ NEW: backend often attaches “hurts/helps” per team, per category, etc.
  // We don’t assume exact shapes — just walk a couple levels and harvest strings.
  walk(row?.hurts, 4);
  walk(row?.helps, 4);
  walk(row?.hurtsByTeam, 5);
  walk(row?.helpsByTeam, 5);
  walk(row?.factorsByTeam, 5);
  walk(row?.team0Hurts, 4);
  walk(row?.team1Hurts, 4);
  walk(row?.team0Helps, 4);
  walk(row?.team1Helps, 4);
  walk(row?.insights, 5);
  walk(row?.gameStory, 5);

  return out;
}

function losingTeamForRow(row: any): Team01 | null {
  const w: any = row?.winnerTeam;
  if (w === 0) return 1;
  if (w === 1) return 0;
  return null;
}

function stakeForTeam(row: any, team: Team01): StakeKey | null {
  const servingTeam: any = row?.servingTeam;
  const scoredPoint: any = row?.scoredPoint;

  if (!(servingTeam === 0 || servingTeam === 1)) return null;
  if (typeof scoredPoint !== "boolean") return null;

  // Opportunity lost: team was serving and lost => no point awarded
  if (scoredPoint === false && team === servingTeam) return "opportunityLost";

  // Game point lost: team was receiving and lost => opponent (server) scored
  if (scoredPoint === true && team !== servingTeam) return "gamePointLost";

  return null;
}
function computeStakeCounts(args: {
  rows: any[];
  // if provided, only consider these rallyIndexes (e.g. those matching selected reasons)
  scopeRallyIndexes?: Set<number> | null;
}): Record<StakeKey, TeamCounts> {
  const out: Record<StakeKey, TeamCounts> = {
    opportunityLost: { t0: 0, t1: 0 },
    gamePointLost: { t0: 0, t1: 0 },
  };

  const scope = args.scopeRallyIndexes;

  (args.rows ?? []).forEach((r) => {
    const idx = r?.rallyIndex;
    if (scope && !scope.has(idx)) return;

    const loser = losingTeamForRow(r);
    if (loser == null) return;

    const st = stakeForTeam(r, loser);
    if (!st) return;

    if (loser === 0) out[st].t0 += 1;
    if (loser === 1) out[st].t1 += 1;
  });

  return out;
}

/**
 * Try to get the PBV “shots” array if it exists on this row.
 * Your own numShots logic already checks row.shots, so we mirror that.
 */
function getShotsArray(row: any): any[] {
  if (Array.isArray(row?.shots)) return row.shots;
  if (Array.isArray(row?.pbvRally?.shots)) return row.pbvRally.shots;
  if (Array.isArray(row?.rally?.shots)) return row.rally.shots;
  return [];
}

/**
 * Heuristic: does a shot object look like an “error/miss”?
 * This is deliberately permissive — tune as you learn your exact shot schema.
 */
function shotLooksLikeMiss(shot: any): boolean {
  const s = shot ?? {};
  const str = (v: any) => (typeof v === "string" ? v.toLowerCase() : "");

  // common flags
  if (s.isError === true || s.error === true || s.fault === true) return true;

  // common fields
  const outcome = str(s.outcome || s.result || s.shotOutcome || s.endingType);
  if (
    outcome.includes("fault") ||
    outcome.includes("miss") ||
    outcome.includes("error") ||
    outcome.includes("out") ||
    outcome.includes("net")
  ) {
    return true;
  }

  const desc = str(s.description || s.reason);
  if (
    desc.includes("fault") ||
    desc.includes("miss") ||
    desc.includes("error") ||
    desc.includes("out") ||
    desc.includes("net")
  ) {
    return true;
  }

  return false;
}

function shotTypeIsDrop(shot: any): boolean {
  const s = (shot?.shotType || shot?.type || shot?.name || "")
    .toString()
    .toLowerCase();
  return s.includes("drop");
}
function shotTypeIsDrive(shot: any): boolean {
  const s = (shot?.shotType || shot?.type || shot?.name || "")
    .toString()
    .toLowerCase();
  return s.includes("drive");
}

/**
 * Decide whether a rally matches each filter bucket.
 * Priority:
 *  1) factor strings already computed by backend
 *  2) fallback: shot-based heuristics (serve=shot1, return=shot2, third=shot3)
 */
// function classifyRallyForStoryFilters(
//   row: any,
// ): Record<StoryFilterKey, boolean> {
//   const factors = getFactorStrings(row).map((s) => s.toLowerCase());
//   const joined = factors.join(" | ");

//   const has = (re: RegExp) => re.test(joined);

//   const fromFactors = {
//     missedServe: has(
//       /miss(?:ed)?\s+serve|serve\s+fault|fault.*serve|serve.*fault/i,
//     ),
//     missedReturn: has(
//       /miss(?:ed)?\s+return|return\s+fault|fault.*return|return.*fault/i,
//     ),

//     // ✅ match “3rd shot”, “third shot”, “shot 3”, etc.
//     missed3rdDrop: has(
//       /(miss(?:ed)?|error|fault).*(3(?:rd)?|third|shot\s*3).*(drop)/i,
//     ),
//     missed3rdDrive: has(
//       /(miss(?:ed)?|error|fault).*(3(?:rd)?|third|shot\s*3).*(drive)/i,
//     ),
//   };

//   // If factor strings already identify *any* of these, trust them and stop.
//   const anyFromFactors =
//     fromFactors.missedServe ||
//     fromFactors.missedReturn ||
//     fromFactors.missed3rdDrop ||
//     fromFactors.missed3rdDrive;

//   if (anyFromFactors) return fromFactors;

//   // ---- fallback: infer from shots array ----
//   const shots = getShotsArray(row);

//   // “shot 1” = serve, “shot 2” = return, “shot 3” = third
//   const s1 = shots[0] ?? null;
//   const s2 = shots[1] ?? null;
//   const s3 = shots[2] ?? null;

//   const missedServe = s1 ? shotLooksLikeMiss(s1) : false;
//   const missedReturn = s2 ? shotLooksLikeMiss(s2) : false;

//   // third shot: only count if we can also recognize drive vs drop
//   const thirdMiss = s3 ? shotLooksLikeMiss(s3) : false;
//   const missed3rdDrop = thirdMiss && shotTypeIsDrop(s3);
//   const missed3rdDrive = thirdMiss && shotTypeIsDrive(s3);

//   return { missedServe, missedReturn, missed3rdDrop, missed3rdDrive };
// }

function incCount(acc: TeamCounts, team: 0 | 1 | null | undefined) {
  if (team === 0) acc.t0 += 1;
  else if (team === 1) acc.t1 += 1;
  acc.total = acc.t0 + acc.t1;
}

function emptyCounts(): TeamCounts {
  return { t0: 0, t1: 0, total: 0 };
}

function getNumShotsLoose(row: any): number | null {
  if (typeof row?.numShots === "number") return row.numShots;
  if (Array.isArray(row?.shots)) return row.shots.length;
  if (typeof row?.shotsCount === "number") return row.shotsCount;
  if (Array.isArray(row?.shotNumberOverlays))
    return row.shotNumberOverlays.length;
  return null;
}

function getThirdShotKindLoose(row: any): "drop" | "drive" | null {
  // 1) direct fields (if you ever send them)
  const s = (row?.shotType ?? row?.thirdShotType ?? row?.third_shot_type ?? "")
    .toString()
    .toLowerCase();
  if (s.includes("drop")) return "drop";
  if (s.includes("drive")) return "drive";

  // 2) overlays: IMPORTANT — prefer structured shotType over label
  const ov3 = Array.isArray(row?.shotNumberOverlays)
    ? row.shotNumberOverlays.find((o: any) => Number(o?.shotNumber) === 3)
    : null;

  const ovShotType = (ov3?.shotType ?? ov3?.shot_type ?? "")
    .toString()
    .toLowerCase();
  if (ovShotType.includes("drop")) return "drop";
  if (ovShotType.includes("drive")) return "drive";

  // 3) fallback to any text (label/text)
  const label = (ov3?.label ?? ov3?.text ?? "").toString().toLowerCase();
  if (label.includes("drop")) return "drop";
  if (label.includes("drive")) return "drive";

  return null;
}

function teamOfPlayerId(pid: any): Team01 | null {
  // common PBV convention: 0/1 = team0, 2/3 = team1
  if (pid === 0 || pid === 1) return 0;
  if (pid === 2 || pid === 3) return 1;
  return null;
}

function getOverlays(row: any): any[] {
  return Array.isArray(row?.shotNumberOverlays) ? row.shotNumberOverlays : [];
}

function overlayHasKind(o: any, kind: string): boolean {
  const ks: any[] = Array.isArray(o?.storyKinds) ? o.storyKinds : [];
  return ks.includes(kind);
}

function findLastOverlay(row: any, pred: (o: any) => boolean): any | null {
  const ovs = getOverlays(row);
  for (let i = ovs.length - 1; i >= 0; i--) {
    const o = ovs[i];
    if (o && pred(o)) return o;
  }
  return null;
}

/**
 * Returns:
 * - match: does this row qualify for this filter?
 * - errTeam: which team *made* the error (used for per-team chip counts)
 */
function matchFilter(
  row: any,
  k: RallyFilterKey,
): { match: boolean; errTeam: 0 | 1 | null } {
  const n = getNumShotsLoose(row);
  const servingTeam: 0 | 1 | null =
    row?.servingTeam === 0 || row?.servingTeam === 1 ? row.servingTeam : null;

  const winnerTeam: 0 | 1 | null =
    row?.winnerTeam === 0 || row?.winnerTeam === 1 ? row.winnerTeam : null;

  const scoredPoint: boolean | null =
    typeof row?.scoredPoint === "boolean" ? row.scoredPoint : null;

  const losingTeam: 0 | 1 | null =
    winnerTeam != null ? otherTeam(winnerTeam) : null;

  // --- Existing heuristics (already “loss-oriented”) ---

  if (k === "missedServe") {
    const match =
      n === 1 &&
      servingTeam != null &&
      winnerTeam != null &&
      servingTeam !== winnerTeam &&
      scoredPoint === false;
    return { match, errTeam: match ? servingTeam : null };
  }

  if (k === "missedReturn") {
    const match =
      n === 2 &&
      servingTeam != null &&
      winnerTeam != null &&
      servingTeam === winnerTeam &&
      scoredPoint === true;
    return { match, errTeam: match ? otherTeam(servingTeam) : null };
  }

  if (k === "missedThirdDrop" || k === "missedThirdDrive") {
    const kind = getThirdShotKindLoose(row);
    const want = k === "missedThirdDrop" ? "drop" : "drive";
    const match =
      n === 3 &&
      servingTeam != null &&
      winnerTeam != null &&
      servingTeam !== winnerTeam &&
      scoredPoint === false &&
      kind === want;

    return { match, errTeam: match ? servingTeam : null };
  }

  // --- NEW: overlay-derived whys (also “loss-oriented”) ---

  // helper: only accept an errTeam if that team actually lost the rally
  const acceptLoss = (errTeam: Team01 | null) => {
    if (errTeam == null) return { match: false, errTeam: null };
    if (losingTeam == null) return { match: false, errTeam: null };
    const match = errTeam === losingTeam;
    return { match, errTeam: match ? errTeam : null };
  };

  if (k === "faultNet") {
    const o = findLastOverlay(
      row,
      (x) => overlayHasKind(x, "fault_net") || x?.faultType === "net",
    );
    const err = teamOfPlayerId(o?.playerId);
    return acceptLoss(err);
  }

  if (k === "faultOut") {
    const o = findLastOverlay(
      row,
      (x) => overlayHasKind(x, "fault_out") || x?.faultType === "out",
    );
    const err = teamOfPlayerId(o?.playerId);
    return acceptLoss(err);
  }

  if (k === "unforcedError") {
    const o = findLastOverlay(row, (x) => overlayHasKind(x, "unforced_error"));
    const err = teamOfPlayerId(o?.playerId);
    return acceptLoss(err);
  }

  if (k === "forcedFault") {
    // In your sample you have both "forced_fault" and "winner_type:forced_fault"
    const o = findLastOverlay(
      row,
      (x) =>
        overlayHasKind(x, "forced_fault") ||
        overlayHasKind(x, "winner_type:forced_fault"),
    );
    // forced-fault is attributed to the *losing* side (the side that faulted),
    // which is usually the opposite team of the hitter on the “winner” shot.
    const hitterTeam = teamOfPlayerId(o?.playerId);
    const err = hitterTeam != null ? otherTeam(hitterTeam) : null;
    return acceptLoss(err);
  }

  if (k === "popupExploited") {
    const o = findLastOverlay(
      row,
      (x) =>
        x?.popupExploited === true ||
        overlayHasKind(x, "popup_exploited") ||
        overlayHasKind(x, "popup:exploited"),
    );
    // popup exploited => losing team is the team that *popped it up* (other team of hitter)
    const hitterTeam = teamOfPlayerId(o?.playerId);
    const err = hitterTeam != null ? otherTeam(hitterTeam) : null;
    return acceptLoss(err);
  }

  return { match: false, errTeam: null };
}

function computeFilterCounts(rows: any[]): Record<RallyFilterKey, TeamCounts> {
  const out: Record<RallyFilterKey, TeamCounts> = {
    missedServe: emptyCounts(),
    missedReturn: emptyCounts(),
    missedThirdDrop: emptyCounts(),
    missedThirdDrive: emptyCounts(),

    faultNet: emptyCounts(),
    faultOut: emptyCounts(),
    unforcedError: emptyCounts(),
    forcedFault: emptyCounts(),
    popupExploited: emptyCounts(),
  };

  for (const r of rows ?? []) {
    (Object.keys(out) as RallyFilterKey[]).forEach((k) => {
      const { match, errTeam } = matchFilter(r, k);
      if (match) incCount(out[k], errTeam);
    });
  }
  return out;
}

type KeyMoment = {
  id: string;
  rallyIndex: number;
  segmentStartMs: number;
  segmentEndMs: number;
  title: string;
  detail: string;
  kind?: string;
  pointIndex?: number | null;
  pointAwardedToTeam?: number | null;
};

type GameStory = {
  winner: number | null;
  finalScore: [number, number];
  keyMoments: KeyMoment[];
};

/**
 * Best-effort accessors so this doesn't explode if your row shape shifts.
 * We intentionally don't type-narrow too hard yet.
 */
function getRunningScore(row: any): [number, number] | null {
  const rs =
    row?.runningScore ?? row?.running_score ?? row?.insight?.running_score;
  if (Array.isArray(rs) && rs.length === 2) {
    const a = Number(rs[0]);
    const b = Number(rs[1]);
    if (Number.isFinite(a) && Number.isFinite(b)) return [a, b];
  }
  return null;
}

function getPointAwardedTeam(row: any): 0 | 1 | null {
  const t =
    row?.pointAwardedTeam ?? // ✅ KitchenArrivalRow
    row?.pointAwardedToTeam ??
    row?.point_awarded_to_team ??
    row?.insight?.point_awarded_to_team ??
    null;

  if (t === 0 || t === 1) return t;
  if (typeof t === "string") {
    const s = t.trim();
    if (s === "T0" || s === "0") return 0;
    if (s === "T1" || s === "1") return 1;
  }
  const n = typeof t === "number" ? t : Number(t);
  return n === 0 || n === 1 ? (n as 0 | 1) : null;
}

function getScoreBeforeAfter(row: any): {
  before: [number, number] | null;
  after: [number, number] | null;
} {
  const b = Array.isArray(row?.scoreBefore) ? row.scoreBefore : null;
  const a = Array.isArray(row?.scoreAfter) ? row.scoreAfter : null;

  const norm = (x: any): [number, number] | null => {
    if (!Array.isArray(x) || x.length !== 2) return null;
    const n0 = Number(x[0]);
    const n1 = Number(x[1]);
    if (!Number.isFinite(n0) || !Number.isFinite(n1)) return null;
    return [n0, n1];
  };

  return { before: norm(b), after: norm(a) };
}

function getPointAwarded(row: any): boolean {
  // ✅ Backend truth: scoredPoint means a point was scored
  if (row?.scoredPoint === true) return true;

  // ✅ Fallback: score delta check (most robust)
  const before = Array.isArray(row?.scoreBefore) ? row.scoreBefore : null;
  const after = Array.isArray(row?.scoreAfter) ? row.scoreAfter : null;

  if (before && after) {
    return before[0] !== after[0] || before[1] !== after[1];
  }

  return false;
}

function getWinningTeam(row: any): number | null {
  const t =
    row?.winningTeam ?? row?.winning_team ?? row?.insight?.winning_team ?? null;
  const n = typeof t === "number" ? t : Number(t);
  return Number.isFinite(n) ? n : null;
}

function getServingTeam(row: any): number | null {
  const t =
    row?.servingTeam ?? row?.serving_team ?? row?.insight?.serving_team ?? null;
  const n = typeof t === "number" ? t : Number(t);
  return Number.isFinite(n) ? n : null;
}

function getServerNumber(row: any): number | null {
  const t =
    row?.serverNumber ??
    row?.server_number ??
    row?.insight?.server_number ??
    null;
  const n = typeof t === "number" ? t : Number(t);
  return Number.isFinite(n) ? n : null;
}

function getRallyIndex(row: any): number | null {
  const n = row?.rallyIndex ?? row?.rally_index ?? row?.rally ?? null;
  const v = typeof n === "number" ? n : Number(n);
  return Number.isFinite(v) ? v : null;
}

function getSegmentBounds(row: any): { start: number; end: number } | null {
  const a =
    row?.segmentStartMs ??
    row?.segment_start_ms ??
    row?.segmentStartMsAbs ??
    null;
  const b =
    row?.segmentEndMs ?? row?.segment_end_ms ?? row?.segmentEndMsAbs ?? null;
  const start = typeof a === "number" ? a : Number(a);
  const end = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(start) && Number.isFinite(end)) return { start, end };
  return null;
}

function computeScoreTimeline(rows: any[]) {
  // scoreBefore/scoreAfter per rallyIndex
  const before = new Map<number, [number, number]>();
  const after = new Map<number, [number, number]>();

  // Current running score (used only as fallback if backend didn't send before/after)
  let s0 = 0;
  let s1 = 0;

  const sorted = [...(Array.isArray(rows) ? rows : [])]
    .filter(Boolean)
    .sort(
      (a, b) => Number(getRallyIndex(a) ?? 0) - Number(getRallyIndex(b) ?? 0),
    );

  for (const r of sorted) {
    const rallyIndex = getRallyIndex(r);
    if (rallyIndex == null) continue;

    const { before: b, after: a } = getScoreBeforeAfter(r);

    // ✅ Prefer backend truth if present
    if (b) {
      before.set(rallyIndex, b);
      // also sync running score so later “fallback” rallies stay consistent
      s0 = b[0];
      s1 = b[1];
    } else if (!before.has(rallyIndex)) {
      before.set(rallyIndex, [s0, s1]);
    }

    if (a) {
      after.set(rallyIndex, a);
      s0 = a[0];
      s1 = a[1];
      continue;
    }

    // ---- fallback path only if backend did NOT provide scoreAfter ----
    if (getPointAwarded(r)) {
      // best-effort: try pointAwardedTeam first, else infer from row.scoreAfter (if any)
      let awarded = getPointAwardedTeam(r);

      // If no awarded team, we can't safely increment; leave score unchanged.
      if (awarded === 0) s0 += 1;
      else if (awarded === 1) s1 += 1;
    }

    after.set(rallyIndex, [s0, s1]);
  }

  return { before, after, final: [s0, s1] as [number, number] };
}

function computeScoreTimeline_old(rows: any[]) {
  // scoreBefore/scoreAfter per rallyIndex
  const before = new Map<number, [number, number]>();
  const after = new Map<number, [number, number]>();

  let s0 = 0;
  let s1 = 0;

  // Defensive: ensure stable chronological order (your rows are usually already sorted)
  const sorted = [...(Array.isArray(rows) ? rows : [])]
    .filter(Boolean)
    .sort(
      (a, b) => Number(getRallyIndex(a) ?? 0) - Number(getRallyIndex(b) ?? 0),
    );

  for (const r of sorted) {
    const rallyIndex = getRallyIndex(r);
    if (rallyIndex == null) continue;

    // "before" score for this rally
    if (!before.has(rallyIndex)) before.set(rallyIndex, [s0, s1]);

    // Only increment if this row represents a point being awarded
    if (getPointAwarded(r)) {
      let awarded = getPointAwardedTeam(r);

      // ✅ fallback: infer from scoreAfter - scoreBefore
      const b = before.get(rallyIndex) ?? [s0, s1];
      const a = Array.isArray(r.scoreAfter) ? r.scoreAfter : null;

      if (awarded == null && a) {
        if (a[0] === b[0] + 1) awarded = 0;
        if (a[1] === b[1] + 1) awarded = 1;
      }

      if (awarded === 0) s0 += 1;
      else if (awarded === 1) s1 += 1;
    }

    // "after" score for this rally
    after.set(rallyIndex, [s0, s1]);
  }

  return { before, after, final: [s0, s1] as [number, number] };
}

/**
 * A first-pass story builder:
 * - Uses running score / point awarded / serving context if present
 * - Adds: biggest run, run-stopper, comeback attempt, close finish
 *
 * IMPORTANT FIX:
 * - Only considers “point-awarded” rows as scoring events.
 *   Otherwise you get bogus “48 in a row” style run lengths when the report rows
 *   include non-point rows.
 */

/* ----------------------------- component ----------------------------- */
export function computeStory(rows: any[]): GameStory {
  const safeRows = Array.isArray(rows) ? rows.filter(Boolean) : [];

  const sorted = [...safeRows]
    .filter(Boolean)
    .sort(
      (a, b) => Number(getRallyIndex(a) ?? 0) - Number(getRallyIndex(b) ?? 0),
    );

  // Representative row per rallyIndex (for segment bounds / jumping)
  const rowByRally = new Map<number, any>();
  for (const r of sorted) {
    const ri = getRallyIndex(r);
    if (ri == null) continue;
    if (!rowByRally.has(ri)) rowByRally.set(ri, r);
  }

  const scoreTimeline = computeScoreTimeline(sorted);
  const finalScore: [number, number] = scoreTimeline.final;

  // Winner: explicit winning_team preferred, else infer from final score
  let winner: number | null = null;
  for (let i = sorted.length - 1; i >= 0; i--) {
    const wt = getWinningTeam(sorted[i]);
    if (wt === 0 || wt === 1) {
      winner = wt;
      break;
    }
  }
  if (winner == null) {
    if (finalScore[0] > finalScore[1]) winner = 0;
    else if (finalScore[1] > finalScore[0]) winner = 1;
  }

  const fmtScore = (s: [number, number] | null | undefined) =>
    s ? `${s[0]}–${s[1]}` : "—";

  const scoreChanged = (
    before: [number, number] | null | undefined,
    after: [number, number] | null | undefined,
  ) => {
    if (!before || !after) return false;
    return before[0] !== after[0] || before[1] !== after[1];
  };

  // Infer awarded team from score delta (authoritative)
  const awardedTeamFromDelta = (
    before: [number, number] | null,
    after: [number, number] | null,
  ): 0 | 1 | null => {
    if (!before || !after) return null;
    const d0 = after[0] - before[0];
    const d1 = after[1] - before[1];
    if (d0 === 1 && d1 === 0) return 0;
    if (d1 === 1 && d0 === 0) return 1;
    return null;
  };

  const getNumShots = (row: any): number | null => {
    const v =
      row?.numShots ??
      row?.num_shots ??
      row?.shotCount ??
      row?.shot_count ??
      row?.numShotsInRally ??
      row?.insight?.num_shots ??
      null;

    if (typeof v === "number" && Number.isFinite(v)) return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const mkByRally = (
    rallyIndex: number,
    title: string,
    detail: string,
    kind?: string,
    pointAwardedToTeam?: 0 | 1 | null,
  ): KeyMoment | null => {
    const r = rowByRally.get(rallyIndex);
    if (!r) return null;

    const seg = getSegmentBounds(r);
    if (!seg) return null;

    return {
      id: `${kind ?? "m"}_${rallyIndex}_${seg.start}`,
      rallyIndex,
      segmentStartMs: seg.start,
      segmentEndMs: seg.end,
      title,
      detail,
      kind,
      pointIndex: rallyIndex, // debug only
      // pointAwardedToTeam: pointAwardedToTeam ?? getPointAwardedTeam(r),

      // Prefer explicit override, else derive from score delta (authoritative)
      pointAwardedToTeam:
        pointAwardedToTeam ??
        awardedTeamFromDelta(
          scoreTimeline.before.get(rallyIndex) ?? null,
          scoreTimeline.after.get(rallyIndex) ?? null,
        ) ??
        getPointAwardedTeam(r),
    };
  };

  type RallyInfo = {
    rallyIndex: number;
    row: any;
    before: [number, number] | null;
    after: [number, number] | null;
    scoredPoint: boolean;
    awardedTo: 0 | 1 | null;
    numShots: number | null;
  };

  const rallies: RallyInfo[] = [];
  const rallyIndices = Array.from(rowByRally.keys()).sort((a, b) => a - b);

  for (const ri of rallyIndices) {
    const r = rowByRally.get(ri);
    if (!r) continue;

    const before = scoreTimeline.before.get(ri) ?? null;
    const after = scoreTimeline.after.get(ri) ?? null;

    const scoredPoint = scoreChanged(before, after);
    const awardedTo = scoredPoint ? awardedTeamFromDelta(before, after) : null;

    rallies.push({
      rallyIndex: ri,
      row: r,
      before,
      after,
      scoredPoint,
      awardedTo,
      numShots: getNumShots(r),
    });
  }

  rallies.sort((a, b) => a.rallyIndex - b.rallyIndex);

  const keyMoments: KeyMoment[] = [];

  // -------------------------
  // Runs (pickleball sane, matching the score strip):
  // "run" = consecutive points by same team (scoring streak).
  // IMPORTANT: run size is derived from score delta from first->last point, not from count.
  // -------------------------
  const scoringRallies = rallies.filter(
    (r) => r.scoredPoint && (r.awardedTo === 0 || r.awardedTo === 1),
  );

  type Run = {
    team: 0 | 1;
    startIdx: number;
    endIdx: number;
  };

  const runs: Run[] = [];
  let curTeam: 0 | 1 | null = null;
  let curStartIdx = -1;

  for (let i = 0; i < scoringRallies.length; i++) {
    const t = scoringRallies[i].awardedTo as 0 | 1 | null;
    if (t !== 0 && t !== 1) continue;

    if (curTeam === t) {
      // continue
    } else {
      // close previous
      if (curTeam != null) {
        runs.push({ team: curTeam, startIdx: curStartIdx, endIdx: i - 1 });
      }
      curTeam = t;
      curStartIdx = i;
    }
  }
  if (curTeam != null && scoringRallies.length > 0) {
    runs.push({
      team: curTeam,
      startIdx: curStartIdx,
      endIdx: scoringRallies.length - 1,
    });
  }

  // Emit only meaningful runs (>= 3 points by score delta)
  for (const run of runs) {
    const startR = scoringRallies[run.startIdx];
    const endR = scoringRallies[run.endIdx];

    const scoreFrom = scoreTimeline.before.get(startR.rallyIndex) ?? null;
    const scoreTo = scoreTimeline.after.get(endR.rallyIndex) ?? null;
    if (!scoreFrom || !scoreTo) continue;

    // run size derived from actual score delta
    const runPoints = scoreTo[run.team] - scoreFrom[run.team];
    if (runPoints < 3) continue;

    // sanity: if score moved weirdly, skip (avoids “(+3)” with +1 score)
    if (runPoints !== scoreTo[run.team] - scoreFrom[run.team]) continue;

    const title = `T${run.team}: ${runPoints}-point run`;
    const detail = `${fmtScore(scoreFrom)} → ${fmtScore(scoreTo)}`;

    const mStart = mkByRally(
      startR.rallyIndex,
      title,
      detail,
      "run_start",
      run.team,
    );
    if (mStart) keyMoments.push(mStart);

    const mEnd = mkByRally(
      endR.rallyIndex,
      `T${run.team}: run ends`,
      detail,
      "run_end",
      run.team,
    );
    if (mEnd) keyMoments.push(mEnd);

    // Sideout that ends the run: first subsequent point by the other team
    const nextIdx = run.endIdx + 1;
    if (nextIdx < scoringRallies.length) {
      const stopR = scoringRallies[nextIdx];
      const stopTeam = stopR.awardedTo;
      if (stopTeam !== run.team && (stopTeam === 0 || stopTeam === 1)) {
        const b = scoreTimeline.before.get(stopR.rallyIndex) ?? null;
        const a = scoreTimeline.after.get(stopR.rallyIndex) ?? null;
        if (b && a && scoreChanged(b, a)) {
          const mStop = mkByRally(
            stopR.rallyIndex,
            `T${stopTeam}: sideout stops their run`,
            `${fmtScore(b)} → ${fmtScore(a)}`,
            "run_stopped",
            stopTeam,
          );
          if (mStop) keyMoments.push(mStop);
        }
      }
    }
  }

  // -------------------------
  // Largest lead checkpoints (team-labeled, only on scored points)
  // -------------------------
  let bestMargin = -1;
  for (const r of scoringRallies) {
    if (!r.before || !r.after) continue;
    const margin = Math.abs(r.after[0] - r.after[1]);
    if (margin <= bestMargin) continue;

    bestMargin = margin;
    if (margin < 3) continue;

    const leader = r.after[0] > r.after[1] ? 0 : 1;
    const m = mkByRally(
      r.rallyIndex,
      `T${leader}: largest lead (${margin})`,
      `${fmtScore(r.before)} → ${fmtScore(r.after)}`,
      "largest_lead",
      r.awardedTo,
    );
    if (m) keyMoments.push(m);
  }

  // -------------------------
  // Longest / shortest rally by shots (neutral, no team claim)
  // -------------------------
  const withShots = rallies.filter((r) => (r.numShots ?? 0) > 0);
  if (withShots.length >= 3) {
    let longest = withShots[0];
    let shortest = withShots[0];

    for (const r of withShots) {
      if ((r.numShots ?? 0) > (longest.numShots ?? 0)) longest = r;
      if ((r.numShots ?? 1e9) < (shortest.numShots ?? 1e9)) shortest = r;
    }

    const shotsDetail = (ri: number): string => {
      const b = scoreTimeline.before.get(ri) ?? null;
      const a = scoreTimeline.after.get(ri) ?? null;
      if (!b) return "";
      if (a && scoreChanged(b, a)) return `${fmtScore(b)} → ${fmtScore(a)}`;
      return `${fmtScore(b)}`;
    };

    const mLong = mkByRally(
      longest.rallyIndex,
      `Longest rally (${longest.numShots} shots)`,
      shotsDetail(longest.rallyIndex),
      "longest_rally",
      longest.awardedTo,
    );
    if (mLong) keyMoments.push(mLong);

    const mShort = mkByRally(
      shortest.rallyIndex,
      `Shortest rally (${shortest.numShots} shots)`,
      shotsDetail(shortest.rallyIndex),
      "shortest_rally",
      shortest.awardedTo,
    );
    if (mShort) keyMoments.push(mShort);
  }

  // -------------------------
  // Final point (jumpable)
  // -------------------------
  if (rallies.length > 0) {
    const last = rallies[rallies.length - 1];
    const mFinal = mkByRally(
      last.rallyIndex,
      `Final point`,
      `Ends ${fmtScore(finalScore)}`,
      "final_point",
      last.awardedTo,
    );
    if (mFinal) keyMoments.push(mFinal);
  }

  // Dedup + sort
  const seen = new Set<string>();
  const deduped = keyMoments
    .filter((m) => {
      if (!m?.id) return false;
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    })
    .sort((a, b) => a.rallyIndex - b.rallyIndex);

  return { winner, finalScore, keyMoments: deduped };
}

function hasStoryFilter(active: StoryFilterKey[], k: StoryFilterKey): boolean {
  return active.includes(k);
}

function toggleStoryFilter(
  active: StoryFilterKey[],
  k: StoryFilterKey,
): StoryFilterKey[] {
  return active.includes(k) ? active.filter((x) => x !== k) : [...active, k];
}

function TeamPipsAndCounts(props: { t0: number; t1: number }) {
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: 999,
          bgcolor: teamRgba(Team_0, 0.95), //) "rgba(80,160,255,0.95)",
        }}
      />
      <Box component="span" sx={{ fontWeight: 800 }}>
        {props.t0}
      </Box>
      {/* <Box component="span" sx={{ opacity: 0.55 }}>–</Box> */}
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: 999,
          // bgcolor: "rgba(120,220,140,0.95)",
          bgcolor: teamRgba(Team_1, 0.95), //) "rgba(80,160,255,0.95)",
        }}
      />
      <Box component="span" sx={{ fontWeight: 800 }}>
        {props.t1}
      </Box>
    </Box>
  );
}

function CountPill(props: {
  n: number;
  bg: string; // rgba(...)
  fg?: string;
  onClick?: () => void;
  selected?: boolean;
}) {
  const { n, bg, fg = "rgba(0,0,0,0.82)", onClick, selected } = props;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 22,
        height: 18,
        px: 0.9,
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 900,
        lineHeight: 1,
        color: fg,
        bgcolor: bg,
        ml: 0.55,

        cursor: "pointer",
        userSelect: "none",

        border: selected
          ? "1px solid rgba(0,0,0,0.55)"
          : "1px solid rgba(0,0,0,0.18)",
        boxShadow: selected ? "0 0 0 2px rgba(255,255,255,0.28)" : "none",

        "&:hover": { filter: "brightness(1.05)" },
      }}
    >
      {n}
    </Box>
  );
}

function CountPill_old(props: {
  n: number;
  bg: string; // rgba(...)
  fg?: string; // text color
}) {
  const { n, bg, fg = "rgba(0,0,0,0.82)" } = props;

  const isZero = n === 0;

  const chipBg = "rgba(255,255,255,0.92)"; // or whatever you like

  const bg0 = bg; //pillBg(bg, isZero, chipBg);

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 22,
        height: 18,
        px: 0.9,
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 900,
        lineHeight: 1,
        color: fg,

        // ✅ subtle background (was likely too saturated)
        bgcolor: bg0, //bg, //isZero ? "rgba(0,0,0,0.06)" : bg,

        // ✅ thin outline helps legibility on light/grey chip bg
        // border: isZero
        //   ? "1px solid rgba(0,0,0,0.08)"
        //   : "1px solid rgba(0,0,0,0.12)",
        textShadow: "1px 1px 1px rgba(255,255,255,0.5)",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.10) inset",

        // ✅ tiny inner highlight keeps it “pill-y” without adding clutter
        // boxShadow: "inset 0 1px 0 rgba(255,255,255,0.65)",
      }}
    >
      {n}
    </Box>
  );
}
